import React from "react";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StageColorDot from "./StageColorDot";
import "./boardPills.css";
import "./BoardCardPreview.css";

const isAgentStage = (stage) =>
  stage?.kind === "AGENT" || Boolean(stage?.agent);

/* Agent stages get a small bot glyph in place of the color dot so agent-powered
   boards/templates are recognizable at a glance; human stages keep the dot. */
function StageMarker({ stage, index }) {
  if (isAgentStage(stage)) {
    return (
      <SmartToyOutlinedIcon
        className="board-card-preview__bot"
        sx={{ fontSize: 11 }}
      />
    );
  }
  return <StageColorDot stage={stage} index={index} />;
}

const PREVIEW_MAX_COLUMNS = 4;
const PREVIEW_BAR_COUNTS = [2, 3, 1, 2];

/* Rows that fit in the fixed-height template hero without clipping: up to 4 when
 * they're all shown, or 3 + a "+N more" chip when there are extras. */
const FLOW_MAX_ROWS = 4;
const FLOW_ROWS_WITH_MORE = 3;

/**
 * Mini preview of a board's stages. Two layouts:
 *
 * - `"columns"` (default): a small strip of kanban columns (each with a colored
 *   stage dot + name and a few placeholder cards). Legacy layout for wide
 *   surfaces where column headers stay legible.
 * - `"flow"`: a compact vertical list with one stage per row (colored dot + full
 *   name on a single line). Used by board list cards and create-board template
 *   cards.
 *
 * Accepts a `stages` array (`{ stageId?, name, order?, color? }`); array order is
 * used when `order` is absent (e.g. preset templates). `maxItems` caps how many
 * stages render before the rest collapse into a "+N more" chip.
 */
export default function BoardCardPreview({
  stages = [],
  layout = "columns",
  maxItems,
  className = "",
}) {
  const ordered = [...stages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (layout === "flow") {
    const cap =
      maxItems ??
      (ordered.length > FLOW_MAX_ROWS ? FLOW_ROWS_WITH_MORE : FLOW_MAX_ROWS);
    const shown = ordered.slice(0, cap);
    const overflow = ordered.length - shown.length;

    return (
      <div
        className={`board-card-preview board-card-preview--flow${className ? ` ${className}` : ""}`}
      >
        {shown.map((stage, i) => (
          <div className="board-card-preview__row" key={stage.stageId || i}>
            <StageMarker stage={stage} index={i} />
            <span className="board-card-preview__row-name">
              {stage.name || "Stage"}
            </span>
          </div>
        ))}
        {overflow > 0 && (
          <div className="board-card-preview__more">+{overflow} more</div>
        )}
      </div>
    );
  }

  const cap = maxItems ?? PREVIEW_MAX_COLUMNS;
  const shown = ordered.slice(0, cap);
  const overflow = ordered.length - shown.length;

  return (
    <div className={`board-card-preview${className ? ` ${className}` : ""}`}>
      {shown.map((stage, i) => (
        <div className="board-card-preview__column" key={stage.stageId || i}>
          <div className="board-card-preview__column-header">
            <StageMarker stage={stage} index={i} />
            <span className="board-card-preview__column-name">
              {stage.name || "Stage"}
            </span>
          </div>
          {Array.from({
            length: PREVIEW_BAR_COUNTS[i % PREVIEW_BAR_COUNTS.length],
          }).map((_, j) => (
            <div className="board-card-preview__card" key={j} />
          ))}
        </div>
      ))}
      {overflow > 0 && <div className="board-card-preview__more">+{overflow}</div>}
    </div>
  );
}
